import { useContext } from 'react';
import AppContext from '../../../AppContext';
import styles from '../QuarterlyRow.module.css';
import HeaderHelmet, { HeaderHelmetProps } from '../../SubSectionHeader/HeaderHelmet';
import { computeComparableQuarterDates } from '../quarterDateUtils';

interface ComparableRowHeaderProps {
  readonly helmet: HeaderHelmetProps;
  readonly showPHS?: boolean;
  readonly showNAA?: boolean;
  readonly showCAS?: boolean;
  readonly showTDP?: boolean;
  readonly showCASPlusAVR?: boolean;
  readonly showPYECAS?: boolean;
  readonly showPYEPHS?: boolean;
  readonly quarter?: string;
  readonly calculateChangePYQ?: boolean;
  readonly calculateChangePYE?: boolean;
  readonly calculateChangePYTD?: boolean;
  readonly calculateChange?: boolean;
  readonly calculatePYIndustryAvg?: boolean;
  /** Row identifiers to exclude from change headers (e.g., ["Pt"] removes "/Pt" from header text) */
  readonly removeFromCalculateChangePYE?: string[];
  readonly removeFromCalculateChangePYQ?: string[];
  /** Subsection-level flags derived by the backend from the change flag + row-type composition. */
  readonly showPercentPointChange?: boolean;
  readonly showDollarChange?: boolean;
}

export default function ComparableRowHeader({
  helmet,
  showPHS,
  showNAA,
  showCAS,
  showTDP,
  showCASPlusAVR,
  showPYECAS,
  showPYEPHS,
  quarter = 'Q3',
  calculateChangePYQ,
  calculateChangePYE,
  calculateChangePYTD,
  calculateChange,
  calculatePYIndustryAvg,
  removeFromCalculateChangePYE = [],
  removeFromCalculateChangePYQ = [],
  showPercentPointChange,
  showDollarChange
}: ComparableRowHeaderProps) {
  const { showBenchmark, year, statementType } = useContext(AppContext);

  const isProperty = statementType?.statementTypeCode === 'P';

  // Active change type driving the header suffix.
  function changeSuffix() {
    if (calculateChangePYE) return 'PYE';
    if (calculateChangePYQ) return 'PYQ';
    if (calculateChangePYTD) return 'PYTD';
    return null;
  }

  // Spec-driven "Pt" removal (removeFromCalculateChange{PYE,PYQ}: ["Pt"]) turns the "%/Pt" prefix
  // into "%" for amount-only subsections. Shared by Property and Life/Health.
  function removePtFor(suffix: string | null) {
    if (suffix === 'PYE') return removeFromCalculateChangePYE.includes('Pt');
    if (suffix === 'PYQ') return removeFromCalculateChangePYQ.includes('Pt');
    return false;
  }

  function hasSurplusHeaders() {
    return showPHS || showCAS || showTDP || showCASPlusAVR || showPYECAS || showPYEPHS;
  }

  function getSurplusHeaderText() {
    if (showPHS) return '% of PHS';
    if (showCAS) return '% of C&S';
    if (showTDP) return '% of Total Direct Premium';
    if (showCASPlusAVR) return '% of CAS +AVR';
    if (showPYECAS) return '% of PYE C&S';
    if (showPYEPHS) return '% of PYE PHS';
    return '';
  }

  function getLifeHealthChangeLabel(suffix: string): string {
    if (removePtFor(suffix)) return `% Change (CQ-${suffix}) / ABS(${suffix})`;
    if (showDollarChange === false) return `Pt. Change (CQ-${suffix})`;
    return `%/Pt Change (CQ-${suffix}) / ABS(${suffix})`;
  }

  function getPercentChangeHeaderText() {
    const suffix = changeSuffix();
    if (isProperty && suffix) {
      const prefix = showDollarChange === false ? 'Pt.' : removePtFor(suffix) ? '%' : '%/Pt.';
      return `${prefix} Change From ${suffix}`;
    }
    if (calculateChangePYQ) return getLifeHealthChangeLabel('PYQ');
    if (calculateChangePYE) return getLifeHealthChangeLabel('PYE');
    if (calculateChangePYTD) return '%/Pt Change From PYTD';
    if (calculateChange) return '% Change';
    return '';
  }

  function getDollarChangeHeaderText() {
    if (isProperty) {
      const suffix = changeSuffix();
      if (suffix) return `$ Change From ${suffix}`;
    }
    if (calculateChangePYQ) return '$ Change (CQ-PYQ)';
    if (calculateChangePYE) return '$ Change (CQ-PYE)';
    if (calculateChangePYTD) return '$ Change From PYTD';
    if (calculateChange) return '$/# Change';
    return '';
  }

  function hasAnyCalculateChange() {
    return calculatePYIndustryAvg || calculateChangePYQ || calculateChangePYE || calculateChangePYTD || calculateChange;
  }

  const dateHeaders = computeComparableQuarterDates(year, quarter);

  // The %/Pt column stays whenever a change flag is set; the $ column drops for RATIO-only
  // subsections (showDollarChange=false).
  const showPctHeader = showPercentPointChange !== false && hasAnyCalculateChange();
  const showDollarHeader = showDollarChange !== false && hasAnyCalculateChange();

  // PY Industry Avg occupies the surplus (3rd) slot — same position as % of PHS / % of C&S.
  const showIndustryAvgHeader = !!calculatePYIndustryAvg;

  return (
    <div
      className={`${styles.container} ${showBenchmark ? '' : styles.collapse}`}
      data-testid="comparable-row-header">
      <HeaderHelmet {...helmet} />
      <h5 className="column_align_right" aria-hidden={!showPctHeader}>
        {showPctHeader ? getPercentChangeHeaderText() : ''}
      </h5>
      <h5 className="column_align_right" aria-hidden={!showDollarHeader}>
        {showDollarHeader ? getDollarChangeHeaderText() : ''}
      </h5>
      <h5 className="column_align_right" aria-hidden={!hasSurplusHeaders() && !showIndustryAvgHeader}>
        {showIndustryAvgHeader ? 'PY Annual Industry Avg' : getSurplusHeaderText()}
      </h5>
      <h5 className="column_align_right" aria-hidden={!showNAA}>
        {showNAA ? '% of NAA' : ''}
      </h5>
      {dateHeaders.map((date) => (
        <h5 key={date} className="column_align_right">
          {date}
        </h5>
      ))}
    </div>
  );
}
